import { useTranslation } from 'react-i18next'
import { PAYMENTS_ENABLED, quotePurchase, type QuoteListingInput } from '@redevicer/shared'

/** peer = greitas apsipirkimas (kontaktas su pardavėju); guarantee = per platformą */
export type BuyChoice = 'peer' | 'guarantee'

type PurchaseRoutePickerProps = {
  listing: QuoteListingInput
  choice: BuyChoice
  onChoiceChange: (choice: BuyChoice) => void
  disabled?: boolean
}

export default function PurchaseRoutePicker({
  listing,
  choice,
  onChoiceChange,
  disabled,
}: PurchaseRoutePickerProps) {
  const { t } = useTranslation()
  const guarantee = quotePurchase(listing, 'guarantee')
  const price = listing.price.toFixed(2)

  const options: {
    id: BuyChoice
    titleKey: string
    taglineKey: string
    total: string
    breakdown?: string
  }[] = [
    {
      id: 'peer',
      titleKey: 'purchase.routePeer',
      taglineKey: 'purchase.routePeerTagline',
      total: `€${price}`,
    },
    ...(PAYMENTS_ENABLED
      ? [
          {
            id: 'guarantee' as const,
            titleKey: 'purchase.routeGuarantee',
            taglineKey: 'purchase.routeGuaranteeTagline',
            total: `€${guarantee.buyerTotalEur.toFixed(2)}`,
            breakdown: [
              t('purchase.devicePrice', { amount: guarantee.devicePriceEur.toFixed(2) }),
              guarantee.shippingEur > 0
                ? t('purchase.shipping', { amount: guarantee.shippingEur.toFixed(2) })
                : null,
              guarantee.inspectionFeeEur > 0
                ? t('purchase.inspection', { amount: guarantee.inspectionFeeEur.toFixed(2) })
                : null,
            ]
              .filter(Boolean)
              .join(' · '),
          },
        ]
      : []),
  ]

  if (!PAYMENTS_ENABLED) {
    return null
  }

  return (
    <div className="purchase-route-picker" role="radiogroup" aria-label={t('purchase.routeLegend')}>
      <p className="purchase-route-picker__legend">{t('purchase.routeLegend')}</p>
      <div className="purchase-route-picker__grid">
        {options.map((opt) => {
          const active = choice === opt.id
          return (
            <label
              key={opt.id}
              className={`purchase-route-card${active ? ' purchase-route-card--active' : ''}`}
            >
              <input
                type="radio"
                name="purchase-route"
                value={opt.id}
                checked={active}
                disabled={disabled}
                onChange={() => onChoiceChange(opt.id)}
              />
              <span className="purchase-route-card__title">{t(opt.titleKey)}</span>
              <span className="purchase-route-card__total">{opt.total}</span>
              <span className="purchase-route-card__tagline">{t(opt.taglineKey)}</span>
            </label>
          )
        })}
      </div>
      {(() => {
        const selected = options.find((opt) => opt.id === choice)
        if (!selected?.breakdown) return null
        return (
          <div className="purchase-route-picker__detail">
            <p className="purchase-route-picker__breakdown">{selected.breakdown}</p>
          </div>
        )
      })()}
    </div>
  )
}
